'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  getSeverityFromScore,
  type FindingCvssVersionKey,
} from '@terra/shared-cvss';
import { Badge } from '../../components/badge';
import { BadgeSeverity } from '../../components/styled/badge-severity';
import {
  HeadingHero,
  LabelDefault,
  MetaDefault,
} from '../../components/typography';

const SCALE_BANDS = [
  { key: 'low', width: '40%', color: token('colors.green.9') },
  { key: 'medium', width: '30%', color: token('colors.yellow.9') },
  { key: 'high', width: '20%', color: token('colors.orange.9') },
  { key: 'critical', width: '10%', color: token('colors.red.9') },
] as const;

const SCORE_COLORS = {
  Critical: token('colors.fg.severity.critical'),
  High: token('colors.fg.severity.high'),
  Medium: token('colors.fg.severity.medium'),
  Low: token('colors.fg.severity.low'),
  Info: token('colors.fg.muted'),
} as const;

export type FindingCvssScoreCardProps = {
  version: FindingCvssVersionKey;
  score: string | null;
  setsSeverity: boolean;
  baseScore?: string | null;
  isEnvironmental?: boolean;
};

export function FindingCvssScoreCard({
  version,
  score,
  setsSeverity,
  baseScore = null,
  isEnvironmental = false,
}: FindingCvssScoreCardProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const value = score === null ? null : Number(score);
  const severity = value === null ? null : getSeverityFromScore(value);

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      flex="1 1 220px"
      minW="220px"
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.subtle"
      bg="bg.subtle"
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <LabelDefault color="fg.muted">
          {t('versionTitle', { version })}
        </LabelDefault>
        {setsSeverity ? (
          <Badge variant="outline" size="sm">
            {t('setsSeverity')}
          </Badge>
        ) : null}
      </HStack>
      <HStack alignItems="baseline" gap={2}>
        <HeadingHero
          style={{ color: severity ? SCORE_COLORS[severity] : undefined }}
        >
          {score ?? '—'}
        </HeadingHero>
        <MetaDefault color="fg.muted">{t('outOfTen')}</MetaDefault>
        {severity ? (
          <BadgeSeverity severity={severity} size="sm">
            {severity}
          </BadgeSeverity>
        ) : (
          <MetaDefault color="fg.muted">{t('notScored')}</MetaDefault>
        )}
      </HStack>
      {isEnvironmental ? (
        <MetaDefault
          data-testid="cvss-score-environmental-caption"
          color="fg.muted"
        >
          {t('environmentalScoreCaption', { baseScore: baseScore ?? '—' })}
        </MetaDefault>
      ) : null}
      <Box position="relative" w="full" pt={1} pb={1}>
        <HStack gap="2px" w="full" h="2" borderRadius="full" overflow="hidden">
          {SCALE_BANDS.map((band) => (
            <Box
              key={band.key}
              h="full"
              style={{ width: band.width, backgroundColor: band.color }}
            />
          ))}
        </HStack>
        {value === null ? null : (
          <Box
            data-testid="cvss-score-marker"
            position="absolute"
            top="0"
            w="4"
            h="4"
            borderRadius="full"
            borderWidth="2px"
            borderColor="bg.default"
            bg="fg.default"
            boxShadow="sm"
            style={{
              left: `calc(${Math.min(Math.max(value, 0), 10) * 10}% - 8px)`,
            }}
          />
        )}
      </Box>
      <HStack justifyContent="space-between">
        <MetaDefault color="fg.muted">0</MetaDefault>
        <MetaDefault color="fg.muted">10</MetaDefault>
      </HStack>
    </VStack>
  );
}
